import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { Eye, ScanLine, ShieldAlert, ShieldCheck } from "lucide-react";
import Counter from "./Counter";
import { EASE_OUT } from "../lib/motion";

/**
 * The sample sentence, taken from the paper's own description of fingerprinting
 * so the demo reads as a real document rather than filler.
 */
const WORDS = ["The", "hash", "values", "of", "the", "input", "document", "are", "matched."];

/** The paper injects the letter 'i' before every word (Figures 8 and 15). */
const INJECTED_CHAR = "i";

/**
 * Measured on the bundled fixture pair — paper_clean.pdf against
 * paper_injected_every.pdf, 'i' before all 73 words. Jaccard on the winnowed
 * fingerprint sets, k=5, t=8. These are real numbers from this codebase, not
 * illustrations.
 */
const MEASURED_BEFORE = 0.125;
const MEASURED_AFTER = 1.0;

const SCAN_MS = 1250;

/** Blank columns between words, and the extra width a hidden glyph adds. */
const WORD_GAP = 4;
const INJECTED_GAP = 3;

function seededHeight(seed) {
  // Deterministic so the profile never changes between renders.
  let value = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    value ^= seed.charCodeAt(i);
    value = Math.imul(value, 16777619);
  }
  return 5 + ((value >>> 0) % 8);
}

/**
 * Build a vertical projection (VPH) for one line from its word structure.
 *
 * Glyphs contribute ink columns; spaces and hidden characters contribute zero
 * columns. The only difference between the two lines is therefore the width of
 * the blank runs — which is precisely the "bigger interval difference for white
 * spaces" the paper observes between its Figures 9 and 10.
 */
function buildProfile(words, injected) {
  const columns = [];
  words.forEach((word, wordIndex) => {
    if (wordIndex > 0) {
      for (let i = 0; i < WORD_GAP; i += 1) columns.push(0);
    }
    if (injected) {
      for (let i = 0; i < INJECTED_GAP; i += 1) columns.push(0);
    }
    Array.from(word).forEach((char, charIndex) => {
      const height = seededHeight(`${wordIndex}:${charIndex}:${char}`);
      columns.push(height, height, 0);
    });
  });
  return columns;
}

function MiniProfile({ values, tone }) {
  const peak = Math.max(...values, 1);
  const fill = tone === "danger" ? "var(--rose-500)" : "var(--accent)";
  return (
    <svg
      className="scope__mini"
      viewBox={`0 0 ${values.length} 14`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {values.map((value, index) =>
        value > 0 ? (
          <rect
            key={index}
            x={index}
            y={14 - (value / peak) * 14}
            width={0.9}
            height={(value / peak) * 14}
            fill={fill}
            opacity={0.75}
          />
        ) : null
      )}
    </svg>
  );
}

/**
 * Interactive hero demo of the character injection attack.
 *
 * The two lines carry identical visible text. The second has the letter 'i'
 * before every word, coloured to match the page and reduced in size — invisible
 * to a reader, but it still occupies horizontal space, which is why its word
 * gaps look wider. Running the scan reveals them, and the projection strips show
 * why a pixel-based reading is unaffected: no ink, no peak.
 */
function InjectionScope() {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -15% 0px" });
  const toggleId = useId();

  const [scanned, setScanned] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [pass, setPass] = useState(0);
  const timer = useRef(0);

  const cleanProfile = useMemo(() => buildProfile(WORDS, false), []);
  const injectedProfile = useMemo(() => buildProfile(WORDS, true), []);

  const visibleText = WORDS.join(" ");
  const rawText = WORDS.map((word) => `${INJECTED_CHAR}${word}`).join(" ");

  const runScan = useCallback(() => {
    window.clearTimeout(timer.current);
    if (prefersReducedMotion) {
      setScanned(true);
      return;
    }
    setPass((value) => value + 1);
    setScanning(true);
    timer.current = window.setTimeout(() => {
      setScanning(false);
      setScanned(true);
    }, SCAN_MS);
  }, [prefersReducedMotion]);

  const onToggle = useCallback(
    (event) => {
      if (event.target.checked) {
        runScan();
      } else {
        window.clearTimeout(timer.current);
        setScanning(false);
        setScanned(false);
      }
    },
    [runScan]
  );

  // Demonstrate once, unprompted, the first time it comes into view.
  useEffect(() => {
    if (!inView || scanned || scanning) return undefined;
    const kickoff = window.setTimeout(runScan, prefersReducedMotion ? 0 : 700);
    return () => window.clearTimeout(kickoff);
    // Keyed on first view only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <div className="scope" ref={ref} data-revealed={scanned}>
      <div className="scope__doc">
        <div className="scope__row">
          <span className="scope__row-tag">source</span>
          <p className="scope__text">{visibleText}</p>
        </div>
        <MiniProfile values={cleanProfile} />

        <div className="scope__row">
          <span className="scope__row-tag" data-tone="danger">
            submitted
          </span>
          <p className="scope__text">
            {WORDS.map((word, index) => (
              <span key={`${word}-${index}`}>
                <span className="scope__ghost">{INJECTED_CHAR}</span>
                {word}
                {index < WORDS.length - 1 ? " " : ""}
              </span>
            ))}
          </p>
        </div>
        <MiniProfile values={injectedProfile} tone="danger" />

        <AnimatePresence>
          {scanning && (
            <motion.span
              key={pass}
              className="scope__scanline"
              initial={{ top: "-10%", opacity: 0 }}
              animate={{ top: "102%", opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: SCAN_MS / 1000, ease: "linear" }}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>
      </div>

      <div className="row row--between row--wrap">
        <label className="switch" htmlFor={toggleId}>
          <input id={toggleId} type="checkbox" checked={scanned || scanning} onChange={onToggle} />
          <span className="switch__track" aria-hidden="true">
            <span className="switch__knob" />
          </span>
          <span className="row" style={{ gap: "var(--space-2)" }}>
            {scanned || scanning ? (
              <Eye size={14} aria-hidden="true" />
            ) : (
              <ScanLine size={14} aria-hidden="true" />
            )}
            {scanning ? "Projecting…" : scanned ? "Injection revealed" : "Run projection"}
          </span>
        </label>

        <AnimatePresence initial={false}>
          {scanned && (
            <motion.span
              className="badge badge--danger"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.24, ease: EASE_OUT }}
            >
              <ShieldAlert size={11} aria-hidden="true" />
              {WORDS.length} hidden ‘{INJECTED_CHAR}’
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* What each reading of the submitted line actually returns. */}
      <div className="scope__extracts">
        <div className="scope__extract" data-tone={scanned ? "bad" : undefined}>
          <span className="scope__readout-label">
            <ShieldAlert size={11} aria-hidden="true" />
            Direct text extract
          </span>
          <code className="scope__extract-text">
            {scanned
              ? WORDS.map((word, index) => (
                  <span key={index}>
                    <mark className="scope__mark">{INJECTED_CHAR}</mark>
                    {word}
                    {index < WORDS.length - 1 ? " " : ""}
                  </span>
                ))
              : rawText}
          </code>
        </div>
        <div className="scope__extract" data-tone={scanned ? "good" : undefined}>
          <span className="scope__readout-label">
            <ShieldCheck size={11} aria-hidden="true" />
            Histogram projection extract
          </span>
          <code className="scope__extract-text">{visibleText}</code>
        </div>
      </div>

      <div className="scope__readouts">
        <div className="scope__readout" data-tone={scanned ? "bad" : undefined}>
          <span className="scope__readout-label">Before · Jaccard</span>
          <span className="scope__readout-value">
            {scanned ? (
              <Counter value={MEASURED_BEFORE} mode="percent" digits={1} suffix="%" />
            ) : (
              "—"
            )}
          </span>
          <span className="meta" style={{ fontSize: "var(--fs-3xs)" }}>
            what an ordinary tool reports
          </span>
        </div>

        <div className="scope__readout" data-tone={scanned ? "good" : undefined}>
          <span className="scope__readout-label">After · Jaccard</span>
          <span className="scope__readout-value">
            {scanned ? <Counter value={MEASURED_AFTER} mode="percent" digits={1} suffix="%" /> : "—"}
          </span>
          <span className="meta" style={{ fontSize: "var(--fs-3xs)" }}>
            once the hidden letters are gone
          </span>
        </div>
      </div>

      <p className="meta" style={{ fontSize: "var(--fs-3xs)" }}>
        Measured on the sample pair in this repo: 73 words, an "i" before each. k=5, t=8.
      </p>
    </div>
  );
}

export default InjectionScope;
